import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import type { ChecklistSource } from "./types";

// ─── Option Definitions ───────────────────────────────────────────────────

interface SourceOption {
  value: ChecklistSource;
  label: string;
  icon: string;
  description: string;
}

const UPLOAD_OPTION: SourceOption = {
  value: "upload",
  label: "Upload My Docs",
  icon: "\uD83D\uDCC4",
  description: "Upload your own documentation to generate a checklist",
};

const OFFICIAL_OPTION: SourceOption = {
  value: "official",
  label: "Official Standard",
  icon: "\u2705",
  description: "Use a pre-built checklist from an official standard",
};

const AI_RESEARCH_OPTION: SourceOption = {
  value: "ai_research",
  label: "AI Research",
  icon: "\uD83E\uDD16",
  description: "Let AI research and generate a checklist",
};

// ─── Component ────────────────────────────────────────────────────────────

interface ChecklistSourceSelectorProps {
  selectedTemplateId: Id<"auditTemplates"> | null;
  value: ChecklistSource | null;
  onChange: (source: ChecklistSource) => void;
}

export function ChecklistSourceSelector({
  selectedTemplateId,
  value,
  onChange,
}: ChecklistSourceSelectorProps) {
  const { data: hasPublished } = useQuery(
    convexQuery(
      api.admin_standards_queries.hasPublishedStandard,
      selectedTemplateId ? { templateId: selectedTemplateId } : "skip",
    ),
  );

  // When no template is selected, show disabled placeholder
  if (!selectedTemplateId) {
    return (
      <div className="rounded-button border border-border/60 bg-card/20 px-4 py-3 text-[13px] text-muted">
        Select an Audit Type first...
      </div>
    );
  }

  // Build available options based on whether an official standard exists
  const options: SourceOption[] = [UPLOAD_OPTION];
  if (hasPublished === true) {
    options.push(OFFICIAL_OPTION);
  }
  options.push(AI_RESEARCH_OPTION);

  return (
    <div role="radiogroup" aria-label="Checklist source" className="flex gap-2">
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            aria-label={`${option.label}: ${option.description}`}
            onClick={() => onChange(option.value)}
            className={`flex flex-1 items-center gap-2 rounded-button border px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer ${
              isSelected
                ? "border-accent bg-accent/10 text-accent"
                : "border-border text-secondary hover:border-accent/30"
            }`}
          >
            <span className="text-base">{option.icon}</span>
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
